import { useCallback, useEffect, useRef, useState } from "react"

export const useDelayedState = <T> (defaultValue: T | (() => T), defaultMinDelay?: number) => {
    const [value, setValue] = useState<T>(defaultValue);

    const lastSetTime = useRef<number>(Date.now());
    const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    
    const minDelayRef = useRef<number>(defaultMinDelay ?? 0);
    minDelayRef.current = defaultMinDelay ?? 0;

    useEffect(() => {
        return () => {
            if (pendingTimeout.current !== null) {
                clearTimeout(pendingTimeout.current);
            }
        };
    }, []);

    const setter = useCallback((newValue: React.SetStateAction<T>, minDelay?: number) => {
        if (pendingTimeout.current !== null) {
            clearTimeout(pendingTimeout.current);
            pendingTimeout.current = null;
        }

        const now = Date.now();
        const delay = Math.max(0, (minDelay ?? minDelayRef.current) - (now - lastSetTime.current));
        
        if (delay) {
            pendingTimeout.current = setTimeout(() => {
                pendingTimeout.current = null;
                setValue(newValue);
                lastSetTime.current = Date.now();
            }, delay);
        } else {
            setValue(newValue);
            lastSetTime.current = now;
        }
    }, []);

    useEffect(() => {
        setter(defaultValue, minDelayRef.current);
    }, [defaultValue, setter]);

    return [value, setter] as const;
}